import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import {
  adjustGroupedMeetingBlocksAcrossTZ,
  classEligibilitySchema,
  CoachAvailabilityItem,
  type CoachPool,
  type CoachPoolFilters,
  coachAvailabilityTypeSchema,
  CoachAvailabilityType,
  coachOverallGradeSchema,
  coachStatusSchema,
  coachSubStatusSchema,
  CoachStatus,
  CoachSubStatus,
  DayOfWeekLabelMap,
  ScheduledClassWithStudentGroup,
  timeRangeSchema,
} from '@airreading/common/data';
import { useFeatureFlag } from '@airreading/feature-flags/web';
import { getCoachPools, getCoachesAvailabilities } from '@/lib/api-calls.js';
import { CoachPoolFilterChips } from './-components/coach-pool-filter-chips.js';
import { coachPoolHasSelectedFilter } from './-components/coach-pool-filter-labels.js';
import { useCoachPoolMutation } from './-components/use-coach-pool-mutation.js';
import { SaveCoachPoolDialog, type CoachPoolSaveReview } from './-components/coach-pool-dialogs.js';
import { PoolDetailsFields, type PoolDetailsValues } from './-components/pool-details-fields.js';
import { TeacherPoolsSection } from './-components/teacher-pools-section.js';
import { validateRouteRole } from '@/lib/routing.js';
import { paginationRequestSchema } from '@/lib/pagination.js';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { InfoPage } from '@/components/info-page.js';
import { coachesSectionTabs } from '../-components/tabs.js';
import {
  formatDateWithDayOfWeek,
  formatSessionDateTimeRangeNumeric,
  FrequentTimeZones,
  isAirAdmin,
  LogicalOperator,
  logicalOperatorOptions,
  logicalOperatorSchema,
  WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES,
} from '@airreading/common';

import { usePaginatedFilters, booleanFilterToSelectedValues, selectedValuesToBooleanFilter } from '@/lib/filtering.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { BackgroundCheckStatusBadgeList } from '../-components/background-check-status-badge-list.js';
import { ClassEligibilityBadgeList } from '../-components/class-eligibility-badge-list.js';
import { CoachOverallGradeBadge } from '@/components/coaches/coach-overall-grade-badge.js';
import {
  getCoachAttendanceGradeLabel,
  coachStatusGroupedOptions,
  getCoachOverallGradeOptions,
  classEligibilityOptions,
  backgroundCheckStatusOptions,
} from '@/lib/coach-utils.js';
import { TeacherStateFilterDropdown } from '../-components/teacher-state-filter-dropdown.js';
import { Temporal } from 'temporal-polyfill';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterDropdownSubItems } from '@/components/table/table-filter-dropdown-sub-items.js';
import { CoachStatusCell } from '../-components/coach-status-cell.js';
import { Badge } from '@/components/ui/badge.js';
import { formatGroupedMeetingBlocksForDisplay } from '@/lib/meeting-block-utils.js';
import { formatWeekPeriodWithTeachingHours } from '@/lib/teaching-hours-utils.js';
import { backgroundCheckStatusSchema } from '@airreading/common/data';
import { buildBooleanSelectOptions } from '@/components/ui/multi-select-utils.js';
import { TableMoreFiltersMenu } from '@/components/table/table-more-filters-menu.js';
import { TableFilterTimeRangePicker } from '@/components/table/table-filter-time-range-picker.js';
import { useMemo, useState, useRef } from 'react';
import { CustomTableDialog } from '@/components/ui/custom-table-dialog.js';
import { ReservedPoolsBadge } from './-components/reserved-pools-badge.js';
import { ScheduledClassesTable } from './-components/scheduled-classes-table.js';
import { TeacherAvailabilityTable } from './-components/teacher-availability-table.js';

const usersSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  statuses: z.array(coachStatusSchema).optional(),
  subStatuses: z.array(coachSubStatusSchema).optional(),
  overallGrades: z.array(coachOverallGradeSchema).optional(),
  classEligibilities: z.array(classEligibilitySchema).optional(),
  backgroundCheckStatuses: z.array(backgroundCheckStatusSchema).optional(),
  backgroundCheckStatusesLogicalOperator: logicalOperatorSchema.optional(),
  googleMeetTechCheck: z.coerce.boolean().optional(),
  hasAvailability: z.coerce.boolean().optional(),
  timeRanges: z.array(timeRangeSchema).optional(),
  untakenTeachersOnly: z.boolean().optional(),
  coachAvailabilityType: coachAvailabilityTypeSchema.optional(),
  states: z.array(z.string().trim().min(1)).optional(),
});

type OpenDialogType = {
  rowId: string;
  type: 'scheduled' | 'availability' | 'sessions' | 'weeksOverTeachingHours';
};

export const Route = createFileRoute('/_layout/teachers/availabilities/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: Availabilities,
  validateSearch: usersSearchSchema,
});

const columnHelper = createColumnHelper<CoachAvailabilityItem>();

const coachesAvailabilitiesTableColumns = (
  openDialog: OpenDialogType | null,
  setOpenDialog: (openDialog: OpenDialogType | null) => void,
  showReservedPools: boolean,
) => [
  createTableRowSelectionCell(columnHelper),

  columnHelper.accessor('coach.fullNameOrEmail', {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <div className="flex flex-col items-start">
        <Link to={'/teachers/$teacherId'} params={{ teacherId: row.original.coach.id }}>
          {getValue()}
        </Link>
        {showReservedPools ? (
          <ReservedPoolsBadge pools={row.original.reservedPools ?? []} coachId={row.original.coach.id} />
        ) : null}
      </div>
    ),
  }),

  columnHelper.accessor('coach', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const coach = getValue();
      return <CoachStatusCell status={coach.status} subStatuses={coach.subStatuses} />;
    },
  }),

  columnHelper.accessor('coach.overallGrade', {
    id: 'overallGrade',
    header: 'Overall grade',
    cell: ({ getValue }) => <CoachOverallGradeBadge grade={getValue()} />,
  }),

  columnHelper.accessor('coach.qualityGrade', {
    id: 'qualityGrade',
    header: 'Teaching Grade',
    cell: ({ getValue }) => getValue(),
  }),

  columnHelper.accessor('coach.attendanceGrade', {
    id: 'attendanceGrade',
    header: 'Attendance grade',
    cell: ({ getValue }) => {
      const attendanceGrade = getValue();
      return getCoachAttendanceGradeLabel(attendanceGrade);
    },
  }),

  columnHelper.accessor('coach.zoomTechCheck', {
    id: 'zoomTechCheck',
    header: 'Zoom Tech Check',
    cell: ({ getValue }) => (getValue() ? '✅' : '❌'),
  }),

  columnHelper.accessor('coach.googleMeetTechCheck', {
    id: 'googleMeetTechCheck',
    header: 'Google Meet Tech Check',
    cell: ({ getValue }) => (getValue() ? '✅' : '❌'),
  }),

  columnHelper.accessor('coach.backgroundCheckStatuses', {
    id: 'backgroundCheckStatuses',
    header: 'Background Check',
    cell: ({ getValue }) => {
      const statuses = getValue();
      return statuses && statuses.length > 0 ? (
        <div className="max-w-lg">
          <BackgroundCheckStatusBadgeList statuses={statuses} />
        </div>
      ) : (
        '-'
      );
    },
  }),

  columnHelper.accessor('coach.classEligibilities', {
    id: 'classEligibilities',
    header: 'Class Eligibilities',
    cell: ({ getValue }) => {
      const eligibilities = getValue();
      return eligibilities && eligibilities.length > 0 ? (
        <div className="max-w-sm">
          <ClassEligibilityBadgeList eligibilities={eligibilities} />
        </div>
      ) : (
        '-'
      );
    },
  }),

  columnHelper.accessor('availabilities', {
    id: 'availabilities',
    header: 'Teacher availability (CT)',
    cell: ({ getValue, row }) => {
      const availabilities = getValue();
      const rowId = row.id;
      const isOpenDialog = openDialog?.rowId === rowId && openDialog?.type === 'availability';

      if (!availabilities || availabilities.length === 0) {
        return '-';
      }

      const uniqueDays = [...new Set(availabilities.map((availability) => availability.day))];
      const dayText = uniqueDays.map((day) => DayOfWeekLabelMap[day].abbr).join(', ');

      const anchor = (
        <div
          className="max-w-sm cursor-pointer hover:underline"
          onClick={() => {
            setOpenDialog({ rowId, type: 'availability' });
          }}
          role="button"
          tabIndex={0}
        >
          {dayText}
        </div>
      );

      return (
        <>
          <CustomTableDialog
            open={isOpenDialog}
            setOpen={(open) => setOpenDialog(open ? { rowId, type: 'availability' } : null)}
            anchor={anchor}
          >
            <TeacherAvailabilityTable
              teacherAvailabilities={availabilities}
              teacherName={row.original.coach.fullNameOrEmail!}
            />
          </CustomTableDialog>
        </>
      );
    },
  }),

  columnHelper.accessor('availabilities', {
    id: 'lastAvailabilityUpdated',
    header: 'Last availability updated',
    cell: ({ getValue }) => {
      const availabilities = getValue();
      if (!availabilities || availabilities.length === 0) {
        return '-';
      }

      const lastUpdatedTimestamp = Math.max(
        ...availabilities.map((availability) => new Date(availability.updatedAt.toString()).getTime()),
      );

      const lastUpdatedInstant = Temporal.Instant.fromEpochMilliseconds(lastUpdatedTimestamp);
      return formatDateWithDayOfWeek(lastUpdatedInstant, FrequentTimeZones.UsCentral);
    },
  }),

  columnHelper.accessor('scheduledClasses', {
    id: 'scheduledClasses',
    header: 'Regular classes (CT)',
    cell: ({ getValue, row }) => {
      const scheduledClasses = getValue();
      const rowId = row.id;
      const isOpenDialog = openDialog?.rowId === rowId && openDialog?.type === 'scheduled';
      const classesToShow = 2;

      // Keep only unique classes by studentGroupId
      const uniqueScheduledClassesMap = new Map(
        scheduledClasses.map((scheduledClass) => [scheduledClass.studentGroupId, scheduledClass]),
      );
      const uniqueScheduledClasses = Array.from(uniqueScheduledClassesMap.values());

      // Helper function to get adjusted meeting blocks
      const getAdjustedMeetingBlocks = (scheduledClass: ScheduledClassWithStudentGroup) => {
        let meetingBlocks = scheduledClass.studentGroup.classTime.groupedMeetingBlocks ?? [];
        if (scheduledClass.studentGroup.classTime.schedule.school.org.timeZone) {
          meetingBlocks = adjustGroupedMeetingBlocksAcrossTZ(
            meetingBlocks,
            scheduledClass.studentGroup.classTime.schedule.school.org.timeZone,
            FrequentTimeZones.UsCentral,
          );
        }
        return meetingBlocks;
      };

      // Build a formatted list for both the badges and the dialog
      const formattedList = (uniqueScheduledClasses as ScheduledClassWithStudentGroup[]).map((scheduledClass) => {
        const meetingBlocks = getAdjustedMeetingBlocks(scheduledClass);
        return formatGroupedMeetingBlocksForDisplay(meetingBlocks);
      });

      // Build structured data for the table component
      const scheduledClassesTableData = (uniqueScheduledClasses as ScheduledClassWithStudentGroup[]).map(
        (scheduledClass) => {
          const meetingBlocks = getAdjustedMeetingBlocks(scheduledClass);
          return {
            classTimes: formatGroupedMeetingBlocksForDisplay(meetingBlocks),
            startPlainDate:
              scheduledClass.studentGroup.overrideStartPlainDate ||
              scheduledClass.studentGroup.classTime.schedule.startPlainDate,
            endPlainDate:
              scheduledClass.studentGroup.overrideEndPlainDate ||
              scheduledClass.studentGroup.classTime.schedule.endPlainDate,
            sessionsLeft: scheduledClass.sessionsLeft,
            studentGroupId: scheduledClass.studentGroupId,
            schoolId: scheduledClass.studentGroup.classTime.schedule.school.id,
          };
        },
      );

      const displayedClasses = formattedList.slice(0, classesToShow);

      const anchor = (
        <div
          className="max-w-sm flex flex-wrap cursor-pointer"
          onClick={() => {
            setOpenDialog({ rowId, type: 'scheduled' });
          }}
          role="button"
          tabIndex={0}
        >
          {displayedClasses.length === 0
            ? '-'
            : displayedClasses.map((formatted, idx) => (
                <Badge key={idx} variant="outline" className="font-normal mr-2 mb-2">
                  {formatted}
                </Badge>
              ))}
          {formattedList.length > classesToShow && (
            <Badge variant="outline" className="font-normal mr-2 mb-2 py-1">
              View all
            </Badge>
          )}
        </div>
      );

      return (
        <>
          <CustomTableDialog
            open={isOpenDialog}
            setOpen={(open) => setOpenDialog(open ? { rowId, type: 'scheduled' } : null)}
            anchor={anchor}
          >
            <ScheduledClassesTable scheduledClasses={scheduledClassesTableData} />
          </CustomTableDialog>
        </>
      );
    },
  }),

  columnHelper.accessor('scheduledSessions', {
    id: 'scheduledSessions',
    header: 'One time classes (CT)',
    cell: ({ getValue, row }) => {
      const scheduledSessions = getValue();
      const rowId = row.id;
      const isOpenDialog = openDialog?.rowId === rowId && openDialog?.type === 'sessions';

      const sessionToShow = 3;
      const displayedSessions = scheduledSessions.slice(0, sessionToShow);
      const isClickable = scheduledSessions.length > sessionToShow;

      const anchor = (
        <div
          className={`max-w-sm flex flex-wrap ${isClickable ? 'cursor-pointer' : ''}`}
          onClick={
            isClickable
              ? () => {
                  setOpenDialog({ rowId, type: 'sessions' });
                }
              : undefined
          }
          role={isClickable ? 'button' : undefined}
          tabIndex={isClickable ? 0 : undefined}
        >
          {displayedSessions.map((scheduledSession) => {
            return (
              <Badge key={scheduledSession.id} variant="outline" className="font-normal mr-2 mb-2">
                {formatSessionDateTimeRangeNumeric(
                  Temporal.Instant.from(scheduledSession.startInstant),
                  Temporal.Instant.from(scheduledSession.endInstant),
                  FrequentTimeZones.UsCentral,
                  false,
                )}
              </Badge>
            );
          })}
          {scheduledSessions.length > sessionToShow && (
            <Badge variant="outline" className="font-normal mr-2 mb-2 py-1">
              View all
            </Badge>
          )}
        </div>
      );

      return (
        <div className="max-w-sm flex flex-wrap">
          {scheduledSessions.length === 0 && '-'}
          <CustomTableDialog
            open={isOpenDialog}
            setOpen={(open) => setOpenDialog(open ? { rowId, type: 'sessions' } : null)}
            anchor={anchor}
          >
            <div className="flex flex-col border border-gray-200 rounded p-2 text-sm">
              {scheduledSessions.map((session, index) => (
                <div key={session.id}>
                  <div className="py-2">
                    {formatSessionDateTimeRangeNumeric(
                      Temporal.Instant.from(session.startInstant),
                      Temporal.Instant.from(session.endInstant),
                      FrequentTimeZones.UsCentral,
                      true,
                    )}
                  </div>
                  {index < scheduledSessions.length - 1 && <div className="border-t border-gray-200 -mx-2" />}
                </div>
              ))}
            </div>
          </CustomTableDialog>
        </div>
      );
    },
  }),

  columnHelper.accessor('weeksOverTeachingHoursLimit', {
    id: 'weeksOverTeachingHoursLimit',
    header: `Weeks over ${WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES / 60} hrs`,
    cell: ({ getValue, row }) => {
      const weeks = getValue();
      const rowId = row.id;
      const isOpenDialog = openDialog?.rowId === rowId && openDialog?.type === 'weeksOverTeachingHours';

      const weeksToShow = 2;
      const displayedWeeks = weeks.slice(0, weeksToShow);
      const isClickable = weeks.length > weeksToShow;

      const weekBadges = displayedWeeks.map((week) => (
        <Badge key={week.weekStart} variant="outline" className="font-normal mr-2 mb-2">
          {formatWeekPeriodWithTeachingHours(week)}
        </Badge>
      ));

      const anchor = isClickable ? (
        <button
          type="button"
          className="max-w-sm flex flex-wrap cursor-pointer text-left"
          onClick={() => {
            setOpenDialog({ rowId, type: 'weeksOverTeachingHours' });
          }}
          aria-label={`View all weeks over ${WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES / 60} hrs`}
          data-testid={`teachers-availabilities-table-button-view-weeks-over-${rowId}`}
        >
          {weekBadges}
          <Badge variant="outline" className="font-normal mr-2 mb-2 py-1">
            View all
          </Badge>
        </button>
      ) : (
        <div className="max-w-sm flex flex-wrap">{weekBadges}</div>
      );

      return (
        <div className="max-w-sm flex flex-wrap">
          {weeks.length === 0 && '-'}
          <CustomTableDialog
            open={isOpenDialog}
            setOpen={(open) => setOpenDialog(open ? { rowId, type: 'weeksOverTeachingHours' } : null)}
            anchor={anchor}
          >
            <div className="flex flex-col border border-gray-200 rounded p-2 text-sm">
              {weeks.map((week, index) => (
                <div key={week.weekStart}>
                  <div className="py-2">{formatWeekPeriodWithTeachingHours(week)}</div>
                  {index < weeks.length - 1 && <div className="border-t border-gray-200 -mx-2" />}
                </div>
              ))}
            </div>
          </CustomTableDialog>
        </div>
      );
    },
  }),
];

function Availabilities() {
  const {
    pageSize,
    pageIndex,
    onPaginationChange,
    resetFilters,
    activeFiltersCount,
    filters: {
      query: [[query, activeQuery], setQuery],
      statuses: [[statusIds, activeStatusIds], setStatusIds],
      subStatuses: [[subStatusIds, activeSubStatusIds], setSubStatusIds],
      overallGrades: [[overallGradeFilter, activeOverallGradeIds], setOverallGradeFilter],
      classEligibilities: [[classEligibilityFilter, activeClassEligibilityIds], setClassEligibilityFilter],
      backgroundCheckStatuses: [
        [backgroundCheckStatusFilter, activeBackgroundCheckStatusIds],
        setBackgroundCheckStatusFilter,
      ],
      backgroundCheckStatusesLogicalOperator: [
        [backgroundCheckStatusesLogicalOperator],
        setBackgroundCheckStatusesLogicalOperator,
      ],
      googleMeetTechCheck: [[googleMeetTechCheck, activeGoogleMeetTechCheck], setGoogleMeetTechCheck],
      hasAvailability: [[hasAvailability, activeHasAvailability], setHasAvailability],
      timeRanges: [[timeRangesFilter], setTimeRangesFilter],
      untakenTeachersOnly: [[isUntakenTeachersOnlySelected], setIsUntakenTeachersOnlySelected],
      coachAvailabilityType: [
        [coachAvailabilityTypeFilter, activeCoachAvailabilityType],
        setCoachAvailabilityTypeFilter,
      ],
      states: [[states, activeStates], setStates],
    },
  } = usePaginatedFilters(
    Route.id,
    [
      'query',
      'statuses',
      'subStatuses',
      'overallGrades',
      'classEligibilities',
      'backgroundCheckStatuses',
      'backgroundCheckStatusesLogicalOperator',
      'googleMeetTechCheck',
      'hasAvailability',
      'timeRanges',
      'untakenTeachersOnly',
      'coachAvailabilityType',
      'states',
    ],
    ['backgroundCheckStatuses', 'googleMeetTechCheck', 'hasAvailability', 'states'],
  );
  const [viewedPoolId, setViewedPoolId] = useState<string | null>(null);
  const [poolFormMode, setPoolFormMode] = useState<'create' | 'edit' | null>(null);
  const [hasPoolName, setHasPoolName] = useState(false);
  const [poolReview, setPoolReview] = useState<CoachPoolSaveReview | null>(null);
  const poolDetailsRef = useRef<PoolDetailsValues>({ name: '', description: '' });
  const editBaselineRef = useRef('');
  const isEditingPool = poolFormMode === 'edit';
  const isPoolFormOpen = poolFormMode != null;
  const currentPoolFilters = (): CoachPoolFilters => ({
    query,
    statuses: statusIds,
    subStatuses: subStatusIds,
    overallGrades: overallGradeFilter,
    classEligibilities: classEligibilityFilter,
    backgroundCheckStatuses: backgroundCheckStatusFilter,
    backgroundCheckStatusesLogicalOperator,
    googleMeetTechCheck,
    hasAvailability,
    timeRanges: timeRangesFilter,
    untakenTeachersOnly: isUntakenTeachersOnlySelected,
    coachAvailabilityType: coachAvailabilityTypeFilter,
    states,
  });
  const canSavePool = hasPoolName && coachPoolHasSelectedFilter(currentPoolFilters());

  const poolDraftKey = () =>
    JSON.stringify({
      name: poolDetailsRef.current.name.trim(),
      description: poolDetailsRef.current.description.trim(),
      filters: currentPoolFilters(),
    });

  const openPoolForm = (mode: 'create' | 'edit', pool?: Pick<CoachPool, 'title' | 'description'> | null) => {
    setHasPoolName(Boolean(pool?.title?.trim()));
    setPoolFormMode(mode);
    if (mode === 'edit' && pool) {
      editBaselineRef.current = JSON.stringify({
        name: pool.title.trim(),
        description: (pool.description ?? '').trim(),
        filters: currentPoolFilters(),
      });
    }
  };

  const closePoolForm = () => {
    setHasPoolName(false);
    setPoolFormMode(null);
    setPoolReview(null);
  };
  const { savePool, isSavingPool } = useCoachPoolMutation();
  const teacherPoolsEnabled = useFeatureFlag('teacher-pool', false);
  const {
    data: teacherPools,
    isPending: teacherPoolsPending,
    isError: teacherPoolsError,
  } = useQuery({
    ...getCoachPools(),
    enabled: teacherPoolsEnabled,
  });
  const activePoolCount = teacherPools?.pools.filter((pool) => pool.isActive).length ?? 0;
  const viewedPool = teacherPools?.pools.find((pool) => pool.id === viewedPoolId) ?? null;
  const hasUnsavedPoolChanges = () => isEditingPool && poolDraftKey() !== editBaselineRef.current;

  const { data, isPending, refetch, error } = useQuery(
    getCoachesAvailabilities({
      pageIndex,
      pageSize,
      filters: {
        query: activeQuery,
        statuses: activeStatusIds,
        subStatuses: activeSubStatusIds,
        overallGrades: activeOverallGradeIds,
        classEligibilities: activeClassEligibilityIds,
        backgroundCheckStatuses: activeBackgroundCheckStatusIds,
        ...(activeBackgroundCheckStatusIds?.length ? { backgroundCheckStatusesLogicalOperator } : {}),
        googleMeetTechCheck: activeGoogleMeetTechCheck ?? googleMeetTechCheck,
        hasAvailability: activeHasAvailability ?? hasAvailability,
        timeRanges: timeRangesFilter,
        untakenTeachersOnly: isUntakenTeachersOnlySelected ?? true,
        coachAvailabilityType:
          coachAvailabilityTypeFilter ?? activeCoachAvailabilityType ?? CoachAvailabilityType.regular,
        states: activeStates,
        ...(viewedPool ? { excludeTestTeachers: true } : {}),
      },
    }),
  );

  const [openDialog, setOpenDialog] = useState<OpenDialogType | null>(null);

  const columns = useMemo(
    () => coachesAvailabilitiesTableColumns(openDialog, setOpenDialog, teacherPoolsEnabled),
    [openDialog, setOpenDialog, teacherPoolsEnabled],
  );

  const table = useReactTable({
    data: data?.coachAvailabilities ?? [],
    columns,
    getRowId: (row) => row.coach.id,
    rowCount: data?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    enableSorting: false,
    manualPagination: true,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
  });

  const setFiltersFromPool = (pool: CoachPool) => {
    setQuery(pool.query ?? '');
    setStatusIds(pool.statuses);
    setSubStatusIds(pool.subStatuses);
    setOverallGradeFilter(pool.overallGrades);
    setClassEligibilityFilter(pool.classEligibilities);
    setBackgroundCheckStatusFilter(pool.backgroundCheckStatuses);
    setBackgroundCheckStatusesLogicalOperator(pool.backgroundCheckStatusesLogicalOperator ?? undefined);
    setGoogleMeetTechCheck(pool.googleMeetTechCheck ?? undefined);
    setHasAvailability(pool.hasAvailability ?? undefined);
    setTimeRangesFilter(pool.timeRanges ?? undefined);
    setIsUntakenTeachersOnlySelected(pool.untakenTeachersOnly ?? undefined);
    setCoachAvailabilityTypeFilter(pool.coachAvailabilityType ?? undefined);
    setStates(pool.states);
  };

  const showSavedPoolFiltersAsChips = viewedPool != null && !isEditingPool;

  const filterComponents = (
    <>
      <TableFilterInputField
        placeholder="Search name or email"
        value={query ?? ''}
        onChange={(e) => setQuery(e.target.value)}
        className={showSavedPoolFiltersAsChips ? 'shrink-0' : undefined}
      />
      {showSavedPoolFiltersAsChips && viewedPool ? (
        <CoachPoolFilterChips filters={{ ...viewedPool, query: null }} className="min-w-0 flex-1" />
      ) : (
        <>
          <TableFilterDropdownSubItems<CoachStatus, CoachSubStatus>
            title="Status"
            options={coachStatusGroupedOptions}
            selectedValues={statusIds ?? []}
            selectedSubValues={subStatusIds ?? []}
            onChange={setStatusIds}
            onSubValuesChange={setSubStatusIds}
          />
          <TableFilterDropdown
            title="Overall Grade"
            options={getCoachOverallGradeOptions()}
            selectedValues={overallGradeFilter ?? []}
            onChange={setOverallGradeFilter}
          />
          <TableFilterTimeRangePicker
            placeholder="Time range (and)"
            selectedRanges={timeRangesFilter ?? []}
            onChange={setTimeRangesFilter}
            untakenTeachersOnly={isUntakenTeachersOnlySelected ?? true}
            onUntakenTeachersOnlyChange={setIsUntakenTeachersOnlySelected}
            coachAvailabilityType={
              coachAvailabilityTypeFilter ?? activeCoachAvailabilityType ?? CoachAvailabilityType.regular
            }
            onCoachAvailabilityTypeChange={setCoachAvailabilityTypeFilter}
            onClear={() => setTimeRangesFilter(undefined)}
          />
          <TableFilterDropdown
            title="Class Eligibility (and)"
            options={classEligibilityOptions}
            selectedValues={classEligibilityFilter ?? []}
            onChange={setClassEligibilityFilter}
          />
          <TableMoreFiltersMenu
            activeFiltersCount={activeFiltersCount}
            filterItems={[
              {
                title: 'Background Check',
                filterComponent: (
                  <TableFilterDropdown
                    title={`Background Check${backgroundCheckStatusesLogicalOperator ? ` (${backgroundCheckStatusesLogicalOperator})` : ''}`}
                    options={backgroundCheckStatusOptions}
                    selectedValues={backgroundCheckStatusFilter ?? []}
                    onChange={setBackgroundCheckStatusFilter}
                    filterOptions={logicalOperatorOptions}
                    defaultFilterOption={backgroundCheckStatusesLogicalOperator}
                    onFilterChange={(filter) => setBackgroundCheckStatusesLogicalOperator(filter as LogicalOperator)}
                  />
                ),
              },
              {
                title: 'Google Meet Tech Check',
                filterComponent: (
                  <TableFilterDropdown
                    title="Google Meet Tech Check"
                    options={buildBooleanSelectOptions()}
                    variant="radio"
                    selectedValues={booleanFilterToSelectedValues(googleMeetTechCheck)}
                    onChange={(vals) => setGoogleMeetTechCheck(selectedValuesToBooleanFilter(vals))}
                  />
                ),
              },
              {
                title: 'Has availability',
                filterComponent: (
                  <TableFilterDropdown
                    title="Has availability"
                    options={buildBooleanSelectOptions()}
                    variant="radio"
                    selectedValues={booleanFilterToSelectedValues(hasAvailability)}
                    onChange={(vals) => setHasAvailability(selectedValuesToBooleanFilter(vals))}
                  />
                ),
              },
              {
                title: 'State',
                filterComponent: <TeacherStateFilterDropdown selectedValues={states} onChange={setStates} />,
              },
            ]}
          />
          <TableFilterResetButton show onClick={resetFilters} />
        </>
      )}
    </>
  );

  return (
    <InfoPage headerText="Teachers" tabs={coachesSectionTabs()}>
      <div className="flex h-full min-h-0 flex-col">
        {teacherPoolsEnabled && (
          <TeacherPoolsSection
            pools={teacherPools?.pools ?? []}
            poolsLoading={teacherPoolsPending}
            poolsError={teacherPoolsError}
            activePoolCount={activePoolCount}
            distinctReservedTeacherCount={teacherPools?.distinctReservedTeacherCount ?? 0}
            isEditingPool={isEditingPool}
            hasUnsavedChanges={hasUnsavedPoolChanges}
            isPoolFormOpen={isPoolFormOpen}
            saveDisabled={!canSavePool || isSavingPool}
            onCreatePool={() => openPoolForm('create')}
            onSavePool={() => {
              if (!poolFormMode || (poolFormMode === 'edit' && !viewedPool)) {
                return;
              }
              setPoolReview({
                mode: poolFormMode,
                id: viewedPool?.id,
                title: poolDetailsRef.current.name.trim(),
                description: poolDetailsRef.current.description,
                isActive: poolFormMode === 'edit' ? Boolean(viewedPool?.isActive) : true,
                savedTeachersCount: poolFormMode === 'edit' ? (viewedPool?.matchingTeachersCount ?? 0) : 0,
                filters: currentPoolFilters(),
              });
            }}
            onCancelCreate={poolFormMode === 'create' ? closePoolForm : undefined}
            onEditPool={() => openPoolForm('edit', viewedPool)}
            onCancelEdit={() => {
              if (viewedPool) {
                setFiltersFromPool(viewedPool);
              }
              closePoolForm();
            }}
            onViewedPoolChange={(pool) => {
              closePoolForm();
              setViewedPoolId(pool?.id ?? null);
              if (pool) {
                setFiltersFromPool(pool);
              } else {
                resetFilters();
              }
            }}
          />
        )}
        {teacherPoolsEnabled && isPoolFormOpen ? (
          <PoolDetailsFields
            ref={poolDetailsRef}
            defaultName={isEditingPool ? viewedPool?.title : ''}
            defaultDescription={isEditingPool ? viewedPool?.description : ''}
            onHasNameChange={setHasPoolName}
          />
        ) : null}
        <SaveCoachPoolDialog
          review={poolReview}
          matchingTeachersCount={isPending ? undefined : data?.totalExcludingTestTeachers}
          isSaving={isSavingPool}
          onOpenChange={(open) => {
            if (!open) {
              setPoolReview(null);
            }
          }}
          onConfirm={() => {
            if (!poolReview) {
              return;
            }
            savePool(
              {
                mode: poolReview.mode,
                id: poolReview.id,
                title: poolReview.title,
                description: poolReview.description,
                filters: poolReview.filters,
              },
              { onSuccess: closePoolForm },
            );
          }}
        />
        <div className="min-h-0 flex-1">
          <EntityTable
            table={table}
            showPagination
            filters={{ filterLeftComponents: filterComponents }}
            filterLeftClassName={showSavedPoolFiltersAsChips ? 'min-w-0' : undefined}
            autoManagedColumnIds={['selection']}
            isLoading={isPending}
            hasError={!!error}
            emptyState="No teacher availabilities found"
            errorState={<TableErrorState refetchAction={refetch} />}
          />
        </div>
      </div>
    </InfoPage>
  );
}
